import { api } from './apiClient';
import { signalBadgesChanged } from './badgeSignal';

/**
 * Notifications. Titles and bodies arrive rendered by the server, so this
 * module holds no Turkish templates. Ids are converted to strings here, the
 * same seam chatService uses.
 */

export const NOTIFICATION_GROUPS = ['messages', 'myListings', 'following', 'reports'] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];
export type NotificationPrefs = Record<NotificationGroup, boolean>;

export interface ApiNotificationItem {
  id: number;
  type: string;
  title: string;
  body: string;
  link: string;
  at: string;
  readAt: string | null;
}

export interface NotificationItem {
  id: string;
  type: string;
  title: string;
  body: string;
  link: string;
  at: string;
  read: boolean;
}

export const toNotificationItem = (raw: ApiNotificationItem): NotificationItem => ({
  id: String(raw.id),
  type: raw.type,
  title: raw.title,
  body: raw.body,
  link: raw.link,
  at: raw.at,
  read: raw.readAt !== null,
});

export async function listNotifications(before?: string | null): Promise<{
  items: NotificationItem[];
  nextCursor: string | null;
  remaining: number;
}> {
  const query = before ? `?before=${encodeURIComponent(before)}` : '';
  const page = await api<{
    items: ApiNotificationItem[];
    nextCursor: string | null;
    remaining: number;
  }>(`/notifications${query}`);
  return {
    items: page.items.map(toNotificationItem),
    nextCursor: page.nextCursor,
    remaining: page.remaining,
  };
}

export async function getUnreadNotificationCount(): Promise<number> {
  const { count } = await api<{ count: number }>('/notifications/unread-count');
  return count;
}

export async function markNotificationRead(id: string): Promise<void> {
  await api<void>(`/notifications/${id}/read`, { method: 'POST' });
  signalBadgesChanged();
}

export async function markAllNotificationsRead(): Promise<void> {
  await api<void>('/notifications/read-all', { method: 'POST' });
  signalBadgesChanged();
}

export async function getNotificationPrefs(): Promise<NotificationPrefs> {
  return api<NotificationPrefs>('/users/me/notification-prefs');
}

export async function updateNotificationPrefs(
  patch: Partial<NotificationPrefs>
): Promise<NotificationPrefs> {
  return api<NotificationPrefs>('/users/me/notification-prefs', { method: 'PATCH', body: patch });
}
